import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import PageShell from "@/components/site/PageShell";
import { REGOLAMENTO } from "@/data/regolamento";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function renderBody(text) {
  return text.split("\n\n").map((para, idx) => {
    const parts = para.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
    return (
      <p key={idx} className="mt-4 first:mt-0">
        {parts.map((p, i) => {
          if (p.startsWith("**") && p.endsWith("**")) {
            return <strong key={i} className="text-[#1a365d] font-semibold">{p.slice(2, -2)}</strong>;
          }
          if (p.startsWith("*") && p.endsWith("*")) {
            return <em key={i}>{p.slice(1, -1)}</em>;
          }
          return p.split("\n").map((line, j, arr) => (
            <span key={`${i}-${j}`}>
              {line}
              {j < arr.length - 1 && <br />}
            </span>
          ));
        })}
      </p>
    );
  });
}

export default function InBetweenRegolamento() {
  const { i18n } = useTranslation();
  const lang = REGOLAMENTO[i18n.language] ? i18n.language : "it";
  const c = REGOLAMENTO[lang];

  return (
    <PageShell>
      <section className="bg-[#0f2440] text-white pt-32 md:pt-40 pb-20 md:pb-28">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <Link
            to="/inbetween/submit"
            data-testid="regolamento-back-link"
            className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.28em] uppercase text-white/70 hover:text-[#d4af37]"
          >
            <ArrowLeft size={12} /> {c.backLink}
          </Link>
          <div className="mt-10 flex items-start gap-6">
            <span className="hidden md:inline-flex h-14 w-14 items-center justify-center border border-[#d4af37] text-[#d4af37] shrink-0">
              <FileText size={20} strokeWidth={1.6} />
            </span>
            <div>
              <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#d4af37] font-medium">
                {c.eyebrow}
              </span>
              <h1
                data-testid="regolamento-title"
                className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.05] tracking-tight"
              >
                {c.titleA} <span className="italic">{c.titleB}</span>.
              </h1>
              <p className="mt-6 max-w-2xl text-white/80 leading-relaxed text-[15.5px] md:text-[17px]">
                {c.subtitle}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={`${API}/inbetween/regolamento/pdf?lang=${lang}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="regolamento-download-current"
                  className="inline-flex items-center gap-2 bg-[#d4af37] hover:bg-[#c79e2e] text-[#0f2440] font-medium text-[11px] tracking-[0.28em] uppercase px-5 py-3 transition-colors"
                >
                  <Download size={14} strokeWidth={1.8} />
                  {lang === "en" ? "Download PDF (EN)" : "Scarica PDF (IT)"}
                </a>
                <a
                  href={`${API}/inbetween/regolamento/pdf?lang=${lang === "en" ? "it" : "en"}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="regolamento-download-other"
                  className="inline-flex items-center gap-2 border border-white/40 hover:border-[#d4af37] text-white hover:text-[#d4af37] font-medium text-[11px] tracking-[0.28em] uppercase px-5 py-3 transition-colors"
                >
                  <Download size={14} strokeWidth={1.8} />
                  {lang === "en" ? "Scarica PDF (IT)" : "Download PDF (EN)"}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-10 space-y-12 md:space-y-14">
          {c.articles.map((a) => (
            <article
              key={a.n}
              data-testid={`regolamento-${a.n.replace(/[^a-z0-9]/gi, "").toLowerCase()}`}
              className="border-t border-[#e2e8f0] pt-10"
            >
              <div className="flex items-baseline gap-4 mb-5">
                <span className="text-[#d4af37] font-display text-base md:text-lg shrink-0">
                  {a.n}
                </span>
                <h2 className="font-display text-2xl md:text-3xl text-[#1a365d] leading-tight">
                  {a.title}
                </h2>
              </div>
              <div className="font-body text-[#1e293b] leading-[1.85] text-[15.5px] md:text-[16.5px]">
                {renderBody(a.body)}
              </div>
            </article>
          ))}

          <div className="border-t border-[#e2e8f0] pt-12 flex flex-wrap gap-4">
            <Link to="/inbetween/submit" className="btn-primary">
              <ArrowLeft size={14} /> {c.submitCta}
            </Link>
            <a
              href={`${API}/inbetween/regolamento/pdf?lang=${lang}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="regolamento-download-bottom"
              className="btn-outline"
            >
              <Download size={14} strokeWidth={1.8} />
              {lang === "en" ? "Download PDF" : "Scarica il PDF"}
            </a>
            <Link to="/articles/in-between-2026-05-04" className="btn-outline">
              {c.discoverCta}
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
